/**
 * The deterministic physics step shared by the client, bots, tests and the level
 * solver: `stepPlayer` advances one PlayerState by one fixed tick (1/TICK_RATE s).
 *
 * Determinism: only + - * /, comparisons and Math.min/max/floor/ceil/abs are used,
 * and all iteration orders are fixed, so V8 and JavaScriptCore produce bit-identical
 * results. Never add Math.random, Date or transcendental functions here.
 *
 * Per-tick order:
 *  0. a moving block the player stands on carries it up or down with it (x is never moved);
 *  1. input: a pending press goes to an overlapping orb first, otherwise to the
 *     mode's own action (cube or robot jump, ball flip, UFO flap, spider teleport);
 *  2. integrate (semi-implicit Euler): vertical velocity, then position; x always
 *     advances by the current speed and is never blocked, so x depends only on the
 *     tick count and speed portals — every surviving run is at the same x at the
 *     same tick (the solver relies on this);
 *  3. triggers: portals (when the centre crosses them), pads, pickups;
 *  4. solids: landing / head contact with snapping (no horizontal push-back);
 *  5. crash (inner box inside a solid) and spikes, saws, rotors and a boss's attacks;
 *  6. leaving the world (pit); 7. bookkeeping; 8. finish line.
 *
 * Moving blocks, saws and rotors are where the level's tick puts them (game/motion.ts): every
 * step meets them as they are at its tick (`s.tick` once it has begun). A landing on a moving
 * block is a crossing relative to it (its face before and now), as on a still one. A boss's
 * attack is a deadly box that is there for a while only, met alike.
 *
 * With a robust CollisionProfile (the level solver) every step also checks that a
 * human acting a little earlier or later would get the same result (see
 * CollisionProfile); that never changes normal play. A copy whose action was k ticks
 * late is, k ticks later, where this run is now — moved on by k ticks of run. So against
 * what moves, every such copy (whole ticks up to ±margin/speed, while copies are apart:
 * `settle`) is tested at its own tick and place; against what stands still, the box
 * widened by the margin covers them all at once.
 *
 * Mode logic is written in gravity-relative terms: g = +1 (normal) or -1, the feet
 * are on the -g side, and "relative velocity" vr = vy * g is positive when moving
 * away from the feet.
 */
import type { Gravity, LevelStart, Mode, PortalType, SpeedKey } from '../level/types';
import {
  BALL,
  COYOTE_TICKS,
  CUBE,
  DT,
  GRAVITY_FLIP_KICK,
  GRAVITY_PORTAL_DAMPING,
  LAND_TOLERANCE,
  MARGIN_Y_CAP,
  MODE_BOXES,
  ORB_BUFFER_TICKS,
  ORB_VELOCITY,
  PAD_VELOCITY,
  PIT_BOUNCE_VELOCITY,
  PRESS_BUFFER_TICKS,
  ROBOT,
  SHIP,
  SPEEDS,
  SPIDER,
  UFO,
  WAVE,
  WORLD_BOTTOM_Y,
  WORLD_TOP_Y,
} from './constants';
import { createPlayerState } from './state';
import {
  type CollisionProfile,
  type DeathCause,
  EXACT_PROFILE,
  NO_MODIFIERS,
  type PhysicsEvent,
  type PhysicsModifiers,
  type PlayerState,
} from './types';
import { type Aabb, type BoxSet, MAX_ROUND_CIRCLES, type Trigger, type World } from './world';

/** Tolerance for "touching is not overlapping" comparisons (absorbs float noise). */
const EPS = 1e-9;

/** Two surfaces this close count as the same height (snapped positions are exact). */
const SAME_HEIGHT = 1e-6;

/** Fired triggers further behind the player than this are forgotten (exceeds any margin). */
const USED_FORGET_DISTANCE = 2;

/** Returned by contact search when only some timing-shifted copies would touch the surface. */
const UNSAFE_CONTACT = Number.POSITIVE_INFINITY;

/** PlayerState.settle while timing-shifted copies may still be in the air (see settleDown). */
const UNSETTLED = 0xffff;

/** Scratch: a moving block's box now and a tick before, a round hazard's circles (no garbage). */
const BOX: Aabb = { x1: 0, y1: 0, x2: 0, y2: 0 };
const PREV_BOX: Aabb = { x1: 0, y1: 0, x2: 0, y2: 0 };
const CIRCLES = new Float64Array(3 * MAX_ROUND_CIRCLES);

/** What each portal changes. */
const PORTAL_EFFECTS: Readonly<
  Record<PortalType, { gravity?: Gravity; mode?: Mode; speed?: SpeedKey }>
> = {
  'gravity-normal': { gravity: 1 },
  'gravity-reverse': { gravity: -1 },
  'mode-cube': { mode: 'cube' },
  'mode-ship': { mode: 'ship' },
  'mode-ball': { mode: 'ball' },
  'mode-ufo': { mode: 'ufo' },
  'mode-wave': { mode: 'wave' },
  'mode-robot': { mode: 'robot' },
  'mode-spider': { mode: 'spider' },
  'speed-0': { speed: 0 },
  'speed-1': { speed: 1 },
  'speed-2': { speed: 2 },
  'speed-3': { speed: 3 },
};

/** Speed portals act on the whole column (ignore y), which keeps x(t) path-independent. */
export function isSpeedPortal(portal: PortalType): boolean {
  return PORTAL_EFFECTS[portal].speed !== undefined;
}

/** A new attempt's state at the level start; `grounded` if the spawn point rests on a surface. */
export function spawnPlayer(world: World, start: LevelStart): PlayerState {
  const state = createPlayerState(start);
  if (isResting(world, state)) {
    state.grounded = true;
    state.coyote = COYOTE_TICKS;
  }
  return state;
}

/**
 * Advances `s` by one tick, in place. `held` is the input during this tick.
 * Does nothing once the run is over (dead or finished). Events are appended to
 * `events` when given (the solver omits it for speed).
 */
export function stepPlayer(
  world: World,
  s: PlayerState,
  held: boolean,
  mods: Readonly<PhysicsModifiers> = NO_MODIFIERS,
  profile: Readonly<CollisionProfile> = EXACT_PROFILE,
  events?: PhysicsEvent[],
): void {
  if (!s.alive || s.finished) return;
  s.tick++;
  // 0. Moving ground (y moved along by `carried`).
  let carried = s.grounded && world.movers.length > 0 ? carry(world, s) : 0;
  const pressed = held && !s.held;
  s.held = held;
  if (pressed) s.pressAge = 0;
  // Air jumps are full whenever the player stands on something.
  if (s.grounded) s.airJumps = mods.airJumps;

  // 1. Input. Orbs get a pending press first (GD: tapping on an orb never also jumps).
  // `acted`: a jump, flip or orb tap, whose exact timing shapes the rest of the arc.
  let acted = s.pressAge >= 0 && s.pressAge < ORB_BUFFER_TICKS && useOrb(world, s, profile, events);
  if (!s.alive) return;
  if (!acted) {
    const action = modeAction(s, held, pressed);
    if (action !== null) {
      if (profile.margin > 0 && !isRobustAction(world, s, action, profile.margin)) {
        kill(s, 'unsafe', events);
        return;
      }
      performModeAction(world, s, action, events);
      // UFO flaps are steering (covered by the vertical margin), not a timed action.
      acted = action !== 'flap';
      // A teleport puts the spider somewhere else: no carry to take back from there.
      if (s.mode === 'spider') carried = 0;
    }
  }
  // Robust: from now on, mistimed copies of this action are elsewhere on their arcs.
  if (acted && profile.margin > 0) s.settle = UNSETTLED;

  // 2. Integrate.
  const prevX = s.x;
  let prevY = s.y;
  integrate(s, held);

  // 3. Triggers (a mode portal may re-anchor y; prevY moves along so contacts stay consistent).
  prevY = applyTriggers(world, s, prevX, prevY, profile, events);
  if (!s.alive) return;

  // 4. Solids.
  resolveContacts(world, s, prevY, carried, profile, events);
  if (!s.alive) return;

  // 5. Crash, spikes, saws, rotors and attacks.
  if (!mods.invulnerable) {
    const { inner, hazard } = MODE_BOXES[s.mode];
    if (
      overlapsBoxes(world.solids, s, inner, profile) ||
      (world.movers.length > 0 && hitsMovers(world, s, inner, profile))
    ) {
      kill(s, 'crash', events);
      return;
    }
    if (
      overlapsBoxes(world.hazards, s, hazard, profile) ||
      (world.rounds.length > 0 && hitsRounds(world, s, hazard, profile)) ||
      (world.attacks.length > 0 && hitsAttacks(world, s, hazard, profile))
    ) {
      kill(s, 'spike', events);
      return;
    }
  }

  // 6. Leaving the world.
  if (s.y < WORLD_BOTTOM_Y || s.y > WORLD_TOP_Y) {
    if (!mods.invulnerable) {
      kill(s, 'pit', events);
      return;
    }
    const fromBelow = s.y < WORLD_BOTTOM_Y;
    s.y = fromBelow ? WORLD_BOTTOM_Y : WORLD_TOP_Y;
    s.vy = fromBelow ? PIT_BOUNCE_VELOCITY : -PIT_BOUNCE_VELOCITY;
    s.grounded = false;
    s.headContact = false;
  }

  // 7. Bookkeeping.
  if (s.grounded) s.coyote = COYOTE_TICKS;
  else if (s.coyote > 0) s.coyote--;
  if (s.pressAge >= 0) {
    s.pressAge++;
    if (s.pressAge >= PRESS_BUFFER_TICKS) s.pressAge = -1;
  }
  if (s.used.length > 0) forgetPassedTriggers(world, s);
  if (s.settle > 0) settleDown(s, profile.margin);

  // 8. Finish line.
  if (s.x >= world.length) {
    s.finished = true;
    events?.push({ type: 'finish' });
  }
}

/** True if the player's feet rest exactly on a solid surface (a moving one where it is now). */
export function isResting(world: World, s: PlayerState): boolean {
  const halfW = MODE_BOXES[s.mode].outer.w / 2;
  return restsAt(world, s.x, halfW, feetY(s), s.gravity, s.tick);
}

/** True if the player's crash box overlaps a solid (e.g. after phasing through a wall with a shield). */
export function isInsideSolid(world: World, s: PlayerState): boolean {
  const inner = MODE_BOXES[s.mode].inner;
  return (
    overlapsBoxes(world.solids, s, inner, EXACT_PROFILE) ||
    (world.movers.length > 0 && hitsMovers(world, s, inner, EXACT_PROFILE))
  );
}

/** True if the player's hazard box overlaps a spike hitbox, a saw, a rotor or an attack. */
export function isTouchingHazard(world: World, s: PlayerState): boolean {
  const hazard = MODE_BOXES[s.mode].hazard;
  return (
    overlapsBoxes(world.hazards, s, hazard, EXACT_PROFILE) ||
    (world.rounds.length > 0 && hitsRounds(world, s, hazard, EXACT_PROFILE)) ||
    (world.attacks.length > 0 && hitsAttacks(world, s, hazard, EXACT_PROFILE))
  );
}

/**
 * Does a surface hold feet at height `feet` (gravity `g`) for a box of half-width `halfW`
 * centred at x, at `tick` — a still block's, or a moving one's where it is then?
 */
function restsAt(
  world: World,
  x: number,
  halfW: number,
  feet: number,
  g: number,
  tick: number,
): boolean {
  if (restsOnStill(world, x, halfW, feet, g)) return true;
  if (world.movers.length === 0) return false;
  const index = world.moverIndex;
  const n = index.query(x - halfW, x + halfW);
  for (let i = 0; i < n; i++) {
    const b = world.moverBox(index.found[i]!, tick, BOX);
    if (!(b.x1 < x + halfW - EPS && b.x2 > x - halfW + EPS)) continue;
    if (Math.abs((g > 0 ? b.y2 : b.y1) - feet) < SAME_HEIGHT) return true;
  }
  return false;
}

/**
 * Where an action of tick `tick` sees the ground: the player stood at x on a still block, or on
 * a moving one where it was a tick before, that has carried the feet to `feet` by now (step 0).
 */
function standsAt(
  world: World,
  x: number,
  halfW: number,
  feet: number,
  g: number,
  tick: number,
): boolean {
  if (restsOnStill(world, x, halfW, feet, g)) return true;
  const index = world.moverIndex;
  const n = index.query(x - halfW, x + halfW);
  for (let i = 0; i < n; i++) {
    const id = index.found[i]!;
    const before = world.moverBox(id, tick - 1, PREV_BOX);
    if (!(before.x1 < x + halfW - EPS && before.x2 > x - halfW + EPS)) continue;
    const now = world.moverBox(id, tick, BOX);
    if (Math.abs((g > 0 ? now.y2 : now.y1) - feet) < SAME_HEIGHT) return true;
  }
  return false;
}

/** restsAt for the blocks that stand still. */
function restsOnStill(world: World, x: number, halfW: number, feet: number, g: number): boolean {
  const solids = world.solids;
  const n = solids.index.query(x - halfW, x + halfW);
  for (let i = 0; i < n; i++) {
    const id = solids.index.found[i]!;
    if (!(solids.x1[id]! < x + halfW - EPS && solids.x2[id]! > x - halfW + EPS)) continue;
    if (Math.abs(floorFace(solids, id, g) - feet) < SAME_HEIGHT) return true;
  }
  return false;
}

/**
 * Step 0: the player stands on a moving block (where it was a tick ago): moves the player up or
 * down as far as the block moved — the one that lifts it most, and a sinking one only if no
 * still ground holds it too. Returns the change of y.
 */
function carry(world: World, s: PlayerState): number {
  const halfW = MODE_BOXES[s.mode].outer.w / 2;
  const feet = feetY(s);
  const g = s.gravity;
  const index = world.moverIndex;
  const n = index.query(s.x - halfW, s.x + halfW);
  // How far the best support moved toward the head (relative to gravity).
  let lift = Number.NaN;
  for (let i = 0; i < n; i++) {
    const id = index.found[i]!;
    const before = world.moverBox(id, s.tick - 1, PREV_BOX);
    if (!(before.x1 < s.x + halfW - EPS && before.x2 > s.x - halfW + EPS)) continue;
    const face = g > 0 ? before.y2 : before.y1;
    if (Math.abs(face - feet) >= SAME_HEIGHT) continue;
    const now = world.moverBox(id, s.tick, BOX);
    const moved = g * ((g > 0 ? now.y2 : now.y1) - face);
    if (Number.isNaN(lift) || moved > lift) lift = moved;
  }
  if (Number.isNaN(lift) || lift === 0) return 0;
  if (lift < 0 && restsOnStill(world, s.x, halfW, feet, g)) return 0;
  s.y += g * lift;
  return g * lift;
}

/**
 * True if a tap now would activate an orb: the player's box overlaps the reach of an
 * orb that has not fired yet in this attempt (e.g. to make that orb glow).
 */
export function isTouchingOrb(world: World, s: Readonly<PlayerState>): boolean {
  return findOrb(world, s, 0) >= 0;
}

// ---------------------------------------------------------------------------
// 1. Input
// ---------------------------------------------------------------------------

/**
 * Lets a pending press activate the first overlapping unused orb. Returns whether an
 * orb fired; with a robust profile the run may end instead (`unsafe`).
 */
function useOrb(
  world: World,
  s: PlayerState,
  profile: Readonly<CollisionProfile>,
  events: PhysicsEvent[] | undefined,
): boolean {
  const m = profile.margin;
  const id = findOrb(world, s, m);
  const t = world.triggers[id];
  if (t?.kind !== 'orb') return false;
  // Robust: every shifted copy must already overlap the orb at the moment of its own
  // (equally shifted) press. A buffered press or a partial overlap would make shifted
  // copies activate it at different points of their arcs; right after a portal,
  // earlier copies would tap it in the old mode.
  if (m > 0) {
    const halfW = MODE_BOXES[s.mode].outer.w / 2;
    const certain =
      s.pressAge === 0 && t.x1 < s.x + halfW - m - EPS && t.x2 > s.x - halfW + m + EPS;
    if (!certain || passedPortalWithin(world, s.x, m)) {
      kill(s, 'unsafe', events);
      return false;
    }
  }
  s.used = [...s.used, id];
  s.pressAge = -1;
  s.thrust = 0;
  if (t.color === 'blue' || t.color === 'green') flipGravity(s);
  s.vy = t.color === 'blue' ? -s.gravity * GRAVITY_FLIP_KICK : s.gravity * ORB_VELOCITY[t.color];
  leaveSurface(s);
  events?.push({ type: 'orb', color: t.color, object: t.object });
  return true;
}

/**
 * Trigger id of the first unused orb whose reach overlaps the player's box widened
 * horizontally by `margin`, or -1.
 */
function findOrb(world: World, s: Readonly<PlayerState>, margin: number): number {
  const { w, h } = MODE_BOXES[s.mode].outer;
  const halfW = w / 2 + margin;
  const halfH = h / 2;
  const index = world.triggerIndex;
  const n = index.query(s.x - halfW, s.x + halfW);
  for (let i = 0; i < n; i++) {
    const id = index.found[i]!;
    const t = world.triggers[id]!;
    if (
      t.kind === 'orb' &&
      !s.used.includes(id) &&
      t.y1 < s.y + halfH - EPS &&
      t.y2 > s.y - halfH + EPS &&
      t.x1 < s.x + halfW - EPS &&
      t.x2 > s.x - halfW + EPS
    ) {
      return id;
    }
  }
  return -1;
}

/**
 * What the mode itself does with the input this tick (orbs aside). A jump is the cube's or the
 * robot's (a thrust), a flip the ball's or the spider's (a teleport).
 */
type ModeAction = 'jump' | 'airJump' | 'flip' | 'airFlip' | 'flap';

function modeAction(s: PlayerState, held: boolean, pressed: boolean): ModeAction | null {
  const fresh = pressed && s.pressAge === 0;
  const onSurface = s.grounded || s.coyote > 0;
  switch (s.mode) {
    case 'cube':
      // Holding jumps again right after landing (GD behaviour); a buffered tap counts too.
      if (onSurface) return held || s.pressAge >= 0 ? 'jump' : null;
      return fresh && s.airJumps > 0 ? 'airJump' : null;
    case 'robot':
      // Every jump is a press of its own: holding is how high the robot goes, so a hold that
      // lasts past the landing does not jump again (a buffered tap still counts).
      if (onSurface) return s.pressAge >= 0 ? 'jump' : null;
      return fresh && s.airJumps > 0 ? 'airJump' : null;
    case 'ball':
    case 'spider':
      if (onSurface) return s.pressAge >= 0 ? 'flip' : null;
      return fresh && s.airJumps > 0 ? 'airFlip' : null;
    case 'ufo':
      return fresh ? 'flap' : null;
    case 'ship':
    case 'wave':
      // Steered continuously in integrate().
      return null;
  }
}

function performModeAction(
  world: World,
  s: PlayerState,
  action: ModeAction,
  events: PhysicsEvent[] | undefined,
): void {
  switch (action) {
    case 'jump':
    case 'airJump':
      if (action === 'airJump') s.airJumps--;
      if (s.mode === 'robot') {
        launch(s, ROBOT.jumpVelocity);
        s.thrust = ROBOT.maxThrustTicks;
        events?.push({ type: 'jump', kind: 'robot' });
        return;
      }
      launch(s, CUBE.jumpVelocity);
      events?.push({ type: 'jump', kind: action === 'jump' ? 'ground' : 'air' });
      return;
    case 'flip':
    case 'airFlip':
      if (action === 'airFlip') s.airJumps--;
      if (s.mode === 'spider') {
        teleport(world, s, events);
        return;
      }
      flipGravity(s);
      s.vy = -s.gravity * BALL.flipKick;
      leaveSurface(s);
      events?.push({ type: 'jump', kind: 'flip' });
      return;
    case 'flap':
      launch(s, UFO.flapVelocity);
      events?.push({ type: 'jump', kind: 'flap' });
      return;
  }
}

/**
 * Robust profile: would a copy of the run acting up to `margin` earlier or later do
 * the very same thing? A jump, ball flip or spider teleport needs the ground under every
 * shifted copy (no take-off from the very edge or in coyote time), and no action may come
 * right after a portal, where earlier copies would still have acted in the old mode,
 * gravity or speed. A teleport also needs every shifted copy to find the same surface on
 * the other side, and the copies still running on for up to `margin` before theirs must
 * come to no harm there. UFO flaps are steering, covered by the vertical margin instead.
 */
function isRobustAction(world: World, s: PlayerState, action: ModeAction, margin: number): boolean {
  if (action === 'flap') return true;
  if (passedPortalWithin(world, s.x, margin)) return false;
  if (action === 'jump' || action === 'flip') {
    if (!s.grounded || !groundCarriesShifts(world, s, margin)) return false;
    if (s.mode !== 'spider') return true;
    // (A teleport next to something moving: its copies would meet it elsewhere. Never robust.)
    if (world.dynamic && movesNear(world, s.x, MODE_BOXES[s.mode].outer.w / 2 + margin + 1)) {
      return false;
    }
    const next = s.x + SPEEDS[s.speed] * DT;
    return (
      farSurface(world, s, margin) !== UNSAFE_CONTACT &&
      !overlapsAt(world.solids, s, next, MODE_BOXES[s.mode].inner, margin) &&
      !overlapsAt(world.hazards, s, next, MODE_BOXES[s.mode].hazard, margin) &&
      !(world.rounds.length > 0 && stillRoundsAt(world, s, next, MODE_BOXES[s.mode].hazard, margin))
    );
  }
  return true;
}

/**
 * Robust: does the surface under the feet extend under every copy shifted by up to ±margin? With
 * a moving block near, every copy shifted by whole ticks is asked at its own tick (an action
 * comes before the tick's move, see standsAt): it must take off from this very height there (and
 * so on the same arc).
 */
function groundCarriesShifts(world: World, s: PlayerState, margin: number): boolean {
  const halfW = MODE_BOXES[s.mode].outer.w / 2;
  const feet = feetY(s);
  if (world.movers.length > 0) {
    const shifts = shiftTicks(margin, s.speed);
    const step = SPEEDS[s.speed] * DT;
    if (world.moverWithin(s.x - halfW - shifts * step, s.x + halfW + shifts * step)) {
      for (let k = -shifts; k <= shifts; k++) {
        if (!standsAt(world, s.x + k * step, halfW, feet, s.gravity, s.tick + k)) return false;
      }
      return true;
    }
  }
  const solids = world.solids;
  const n = solids.index.query(s.x - halfW - margin, s.x + halfW + margin);
  return coversAllShifts(
    solids,
    n,
    s.x,
    halfW,
    margin,
    (id) => Math.abs(floorFace(solids, id, s.gravity) - feet) < SAME_HEIGHT,
  );
}

/** Did the player's centre pass a portal's middle less than `margin` ago? */
function passedPortalWithin(world: World, x: number, margin: number): boolean {
  const index = world.triggerIndex;
  const n = index.query(x - margin - 1, x);
  for (let i = 0; i < n; i++) {
    const t = world.triggers[index.found[i]!]!;
    if (t.kind === 'portal' && t.centerX > x - margin && t.centerX <= x) return true;
  }
  return false;
}

function launch(s: PlayerState, relativeVelocity: number): void {
  s.vy = s.gravity * relativeVelocity;
  leaveSurface(s);
}

/** The player actively left the surface: no coyote jump, the press is used up. */
function leaveSurface(s: PlayerState): void {
  s.grounded = false;
  s.headContact = false;
  s.coyote = 0;
  s.pressAge = -1;
}

function flipGravity(s: PlayerState): void {
  s.gravity = s.gravity > 0 ? -1 : 1;
  s.thrust = 0;
}

/**
 * Spider: flips gravity and puts the feet straight onto the nearest surface on the head side
 * (see farSurface), standing there within the same tick. With nothing there it shoots off
 * toward that side (and out of the world).
 */
function teleport(world: World, s: PlayerState, events: PhysicsEvent[] | undefined): void {
  const surface = farSurface(world, s, 0);
  flipGravity(s);
  leaveSurface(s);
  if (Number.isNaN(surface)) {
    s.vy = -s.gravity * SPIDER.maxFall;
  } else {
    // The new feet (on the old head side) on the surface.
    s.y = surface + (s.gravity * MODE_BOXES[s.mode].outer.h) / 2;
    s.vy = 0;
    s.grounded = true;
  }
  events?.push({ type: 'jump', kind: 'teleport' });
}

/**
 * Spider: the face of the nearest solid across its box's column on the head side (one the head
 * touches counts), where a teleport puts its feet; NaN if there is none. Robust profile: every
 * copy shifted by up to ±margin must find that very face under its whole box, else
 * UNSAFE_CONTACT.
 */
function farSurface(world: World, s: Readonly<PlayerState>, margin: number): number {
  const { w, h } = MODE_BOXES[s.mode].outer;
  const halfW = w / 2;
  const dir = s.gravity;
  const head = s.y + (dir * h) / 2;
  const solids = world.solids;
  const n = solids.index.query(s.x - halfW - margin, s.x + halfW + margin);
  let best = Number.NaN;
  let bestExact = Number.NaN;
  for (let i = 0; i < n; i++) {
    const id = solids.index.found[i]!;
    const sx1 = solids.x1[id]!;
    const sx2 = solids.x2[id]!;
    if (!(sx1 < s.x + halfW + margin - EPS && sx2 > s.x - halfW - margin + EPS)) continue;
    const face = facingSurface(solids, id, dir);
    if (dir * (face - head) < -SAME_HEIGHT) continue;
    if (Number.isNaN(best) || dir * face < dir * best) best = face;
    const exact = sx1 < s.x + halfW - EPS && sx2 > s.x - halfW + EPS;
    if (exact && (Number.isNaN(bestExact) || dir * face < dir * bestExact)) bestExact = face;
  }
  if (margin === 0) {
    if (world.movers.length === 0) return bestExact;
    // Moving blocks where they were as the tick began (a teleport comes before its move).
    const index = world.moverIndex;
    const m = index.query(s.x - halfW, s.x + halfW);
    for (let i = 0; i < m; i++) {
      const b = world.moverBox(index.found[i]!, s.tick - 1, BOX);
      if (!(b.x1 < s.x + halfW - EPS && b.x2 > s.x - halfW + EPS)) continue;
      const face = dir < 0 ? b.y2 : b.y1;
      if (dir * (face - head) < -SAME_HEIGHT) continue;
      if (Number.isNaN(bestExact) || dir * face < dir * bestExact) bestExact = face;
    }
    return bestExact;
  }
  // A shifted copy would find another surface, or none.
  if (Number.isNaN(bestExact) !== Number.isNaN(best)) return UNSAFE_CONTACT;
  if (Number.isNaN(best)) return best;
  if (Math.abs(best - bestExact) >= SAME_HEIGHT) return UNSAFE_CONTACT;
  const isBest = (id: number) => Math.abs(facingSurface(solids, id, dir) - best) < SAME_HEIGHT;
  return coversAllShifts(solids, n, s.x, halfW, margin, isBest) ? best : UNSAFE_CONTACT;
}

// ---------------------------------------------------------------------------
// 2. Integration
// ---------------------------------------------------------------------------

function integrate(s: PlayerState, held: boolean): void {
  let vr = s.vy * s.gravity;
  switch (s.mode) {
    case 'cube':
      vr = Math.max(vr - CUBE.gravity * DT, -CUBE.maxFall);
      break;
    case 'robot':
      // Thrust while held (always for the first minThrustTicks), once let go: falling.
      if (s.thrust > 0 && (held || s.thrust > ROBOT.maxThrustTicks - ROBOT.minThrustTicks)) {
        vr = ROBOT.jumpVelocity;
        s.thrust--;
      } else {
        s.thrust = 0;
        vr = Math.max(vr - ROBOT.gravity * DT, -ROBOT.maxFall);
      }
      break;
    case 'spider':
      vr = Math.max(vr - SPIDER.gravity * DT, -SPIDER.maxFall);
      break;
    case 'ball':
      vr = Math.max(vr - BALL.gravity * DT, -BALL.maxFall);
      break;
    case 'ufo':
      vr = Math.max(vr - UFO.gravity * DT, -UFO.maxFall);
      break;
    case 'ship':
      vr += (held ? SHIP.lift : -SHIP.gravity) * DT;
      vr = Math.min(Math.max(vr, -SHIP.maxSpeed), SHIP.maxSpeed);
      break;
    case 'wave': {
      const v = SPEEDS[s.speed] * WAVE.slope;
      vr = held ? v : -v;
      break;
    }
  }
  s.vy = vr * s.gravity;
  s.y += s.vy * DT;
  s.x += SPEEDS[s.speed] * DT;
}

// ---------------------------------------------------------------------------
// 3. Triggers
// ---------------------------------------------------------------------------

function applyTriggers(
  world: World,
  s: PlayerState,
  prevX: number,
  prevY: number,
  profile: Readonly<CollisionProfile>,
  events: PhysicsEvent[] | undefined,
): number {
  const m = profile.margin;
  const index = world.triggerIndex;
  const n = index.query(prevX - 1 - m, s.x + 1 + m);
  let y0 = prevY;
  for (let i = 0; i < n; i++) {
    const id = index.found[i]!;
    const t = world.triggers[id]!;
    if (t.kind === 'portal') {
      if (!(prevX < t.centerX && s.x >= t.centerX)) continue;
      // Robust: copies whose last action was mistimed may still be elsewhere on their
      // arc (see settleDown). They would enter the new mode, gravity or speed in a
      // different state (e.g. at other heights of a jump into a ship portal), or even
      // pass above or below a portal that this run goes through, and vice versa.
      if (m > 0 && s.settle > 0 && portalChanges(t.portal, s)) {
        kill(s, 'unsafe', events);
        return y0;
      }
      if (portalCatches(t, s)) {
        y0 += enterPortal(s, t.portal);
        events?.push({ type: 'portal', portal: t.portal, object: t.object });
      }
    } else if (t.kind === 'pad' || t.kind === 'pickup') {
      if (s.used.includes(id)) continue;
      const { w, h } = MODE_BOXES[s.mode].outer;
      if (!(t.y1 < s.y + h / 2 - EPS && t.y2 > s.y - h / 2 + EPS)) continue;
      const touching = t.x1 < s.x + w / 2 - EPS && t.x2 > s.x - w / 2 + EPS;
      if (t.kind === 'pad') {
        // Robust: a pad touched in mid-air would launch shifted copies from different
        // heights. On the ground every copy meets it at the same spot, which is fine.
        if (m > 0 && !s.grounded && t.x1 < s.x + w / 2 + m - EPS && t.x2 > s.x - w / 2 - m + EPS) {
          kill(s, 'unsafe', events);
          return y0;
        }
        if (touching) firePad(s, t, id, events);
      } else if (touching) {
        s.used = [...s.used, id];
        events?.push({ type: 'pickup', boost: t.boost, object: t.object });
      }
    }
  }
  return y0;
}

function portalCatches(t: Extract<Trigger, { kind: 'portal' }>, s: PlayerState): boolean {
  if (isSpeedPortal(t.portal)) return true;
  const halfH = MODE_BOXES[s.mode].outer.h / 2;
  return t.y1 < s.y + halfH - EPS && t.y2 > s.y - halfH + EPS;
}

/** Would passing this portal change anything (mode, gravity or speed)? */
function portalChanges(portal: PortalType, s: PlayerState): boolean {
  const effect = PORTAL_EFFECTS[portal];
  return (
    (effect.gravity !== undefined && effect.gravity !== s.gravity) ||
    (effect.mode !== undefined && effect.mode !== s.mode) ||
    (effect.speed !== undefined && effect.speed !== s.speed)
  );
}

/** Applies a portal; returns how far y was moved to keep the feet in place. */
function enterPortal(s: PlayerState, portal: PortalType): number {
  const effect = PORTAL_EFFECTS[portal];
  if (effect.speed !== undefined) s.speed = effect.speed;
  if (effect.gravity !== undefined && effect.gravity !== s.gravity) {
    s.gravity = effect.gravity;
    s.thrust = 0;
    s.vy *= GRAVITY_PORTAL_DAMPING;
    s.grounded = false;
    s.headContact = false;
    s.coyote = 0;
  }
  if (effect.mode === undefined || effect.mode === s.mode) return 0;
  // Keep the feet where they are when the box height changes (e.g. wave <-> cube).
  const dy = (s.gravity * (MODE_BOXES[effect.mode].outer.h - MODE_BOXES[s.mode].outer.h)) / 2;
  s.y += dy;
  s.mode = effect.mode;
  s.coyote = 0;
  s.thrust = 0;
  // A press buffered for the old mode must not fire the new mode's action.
  s.pressAge = -1;
  // Keep the vertical speed, limited to what the new mode allows.
  const vr = s.vy * s.gravity;
  s.vy = clampRelativeVelocity(effect.mode, vr) * s.gravity;
  return dy;
}

function clampRelativeVelocity(mode: Mode, vr: number): number {
  switch (mode) {
    case 'cube':
      return Math.max(vr, -CUBE.maxFall);
    case 'robot':
      return Math.max(vr, -ROBOT.maxFall);
    case 'spider':
      return Math.max(vr, -SPIDER.maxFall);
    case 'ball':
      return Math.max(vr, -BALL.maxFall);
    case 'ufo':
      return Math.max(vr, -UFO.maxFall);
    case 'ship':
      return Math.min(Math.max(vr, -SHIP.maxSpeed), SHIP.maxSpeed);
    case 'wave':
      // Set from the input on every tick anyway.
      return vr;
  }
}

function firePad(
  s: PlayerState,
  t: Extract<Trigger, { kind: 'pad' }>,
  id: number,
  events: PhysicsEvent[] | undefined,
): void {
  s.used = [...s.used, id];
  s.thrust = 0;
  if (t.color === 'blue') {
    flipGravity(s);
    s.vy = -s.gravity * GRAVITY_FLIP_KICK;
  } else if (s.mode !== 'wave') {
    // The wave's vertical speed comes from the input alone.
    s.vy = s.gravity * PAD_VELOCITY[t.color];
  }
  s.grounded = false;
  s.headContact = false;
  s.coyote = 0;
  events?.push({ type: 'pad', color: t.color, object: t.object });
}

function forgetPassedTriggers(world: World, s: PlayerState): void {
  const limit = s.x - USED_FORGET_DISTANCE;
  if (s.used.every((id) => world.triggers[id]!.x2 >= limit)) return;
  s.used = s.used.filter((id) => world.triggers[id]!.x2 >= limit);
}

/**
 * Robust profile: counts down to the moment the timing-shifted copies of the run are
 * identical again. x never depends on the input, so copies differ only in their
 * vertical state, and only after a jump, flip or orb tap (which sets UNSETTLED): from
 * then on each copy is at another point of its arc. Once this run has landed (the
 * landing checks make sure every copy lands on the same surface), the latest copy
 * lands at most `margin / speed` later; after that many ticks on the ground, all
 * copies are the same again. Leaving the ground before that starts the wait anew.
 */
function settleDown(s: PlayerState, margin: number): void {
  const lag = Math.ceil(margin / (SPEEDS[s.speed] * DT) - EPS);
  s.settle = s.grounded ? Math.min(s.settle, lag + 1) - 1 : UNSETTLED;
}

// ---------------------------------------------------------------------------
// 4. Solid contacts
// ---------------------------------------------------------------------------

/**
 * Landing (moving toward the feet) or head contact (moving away from them). A
 * contact snaps the box onto the surface and stops vertical motion; nothing ever
 * pushes the player back horizontally (walls are handled by the crash check).
 */
function resolveContacts(
  world: World,
  s: PlayerState,
  prevY: number,
  carried: number,
  profile: Readonly<CollisionProfile>,
  events: PhysicsEvent[] | undefined,
): void {
  const { w, h } = MODE_BOXES[s.mode].outer;
  const halfH = h / 2;
  const g = s.gravity;
  const towardFeet = s.vy * g <= 0;
  // World direction of motion we may have to stop: -g toward the feet, +g toward the head.
  const dir = towardFeet ? -g : g;
  const wasTouching = towardFeet ? s.grounded : s.headContact;
  s.grounded = false;
  s.headContact = false;
  const lead = s.y + dir * halfH;
  const prevLead = prevY + dir * halfH;
  const surface =
    world.movers.length > 0 && moversNear(world, s, w / 2, profile)
      ? findContactMoving(world, s, w / 2, profile, lead, prevLead, carried, dir, wasTouching)
      : findContact(world, s.x, w / 2, profile.margin, lead, prevLead, dir, wasTouching);
  if (surface === UNSAFE_CONTACT) {
    kill(s, 'unsafe', events);
    return;
  }
  if (Number.isNaN(surface)) return;
  s.y = surface - dir * halfH;
  s.vy = 0;
  // A robot's thrust ends on any contact (a head bump stops the jump).
  s.thrust = 0;
  if (towardFeet) {
    s.grounded = true;
    if (!wasTouching) events?.push({ type: 'land' });
  } else {
    s.headContact = true;
  }
}

/**
 * Finds the first surface the leading edge crossed this tick, moving in world
 * direction `dir`. Returns NaN for none, UNSAFE_CONTACT if (robust profile) only some
 * shifted copies of the player would touch it. `wasTouching` = the player rested on
 * that side last tick; sliding along the same surface needs no robustness check.
 */
function findContact(
  world: World,
  x: number,
  halfW: number,
  margin: number,
  lead: number,
  prevLead: number,
  dir: number,
  wasTouching: boolean,
): number {
  const solids = world.solids;
  const n = solids.index.query(x - halfW - margin, x + halfW + margin);
  let best = Number.NaN;
  let bestExact = Number.NaN;
  for (let i = 0; i < n; i++) {
    const id = solids.index.found[i]!;
    const sx1 = solids.x1[id]!;
    const sx2 = solids.x2[id]!;
    if (!(sx1 < x + halfW + margin - EPS && sx2 > x - halfW - margin + EPS)) continue;
    const surface = facingSurface(solids, id, dir);
    if (!crossedSurface(surface, lead, prevLead, dir)) continue;
    const exact = sx1 < x + halfW - EPS && sx2 > x - halfW + EPS;
    // A solid only a shifted copy touches matters if this tick truly crossed its surface.
    // (Right after walking off a ledge the feet are already below it, and every shifted
    // copy walks off that same edge, so it is no divergence.)
    if (!exact && dir * (prevLead - surface) > 0) continue;
    if (Number.isNaN(best) || dir * surface < dir * best) best = surface;
    if (exact && (Number.isNaN(bestExact) || dir * surface < dir * bestExact)) bestExact = surface;
  }
  if (margin === 0 || Number.isNaN(best)) return bestExact;
  // Sliding along (or walking off) the surface touched last tick: every shifted copy
  // leaves an edge at the same place, so this needs no robustness check.
  if (wasTouching && Math.abs(best - prevLead) < SAME_HEIGHT) return bestExact;
  const isBest = (id: number) => {
    const surface = facingSurface(solids, id, dir);
    return Math.abs(surface - best) <= EPS && crossedSurface(surface, lead, prevLead, dir);
  };
  return coversAllShifts(solids, n, x, halfW, margin, isBest) ? best : UNSAFE_CONTACT;
}

/** The leading edge is past `surface` now, and was no more than LAND_TOLERANCE past it last tick. */
function crossedSurface(surface: number, lead: number, prevLead: number, dir: number): boolean {
  return dir * (lead - surface) > 0 && dir * (prevLead - surface) <= LAND_TOLERANCE;
}

/**
 * Does the contact search of this step have to look at moving blocks: is one within reach of the
 * player's box, or of a timing-shifted copy's (see copiesApart)?
 */
function moversNear(
  world: World,
  s: PlayerState,
  halfW: number,
  profile: Readonly<CollisionProfile>,
): boolean {
  const reach = halfW + copiesApart(profile, s) * SPEEDS[s.speed] * DT;
  return world.moverWithin(s.x - reach, s.x + reach);
}

/**
 * findContact with moving blocks near: the nearest surface the leading edge crossed — a moving
 * block's relative to where it was a tick before (`prevLead` is in the frame step 0 carried the
 * player to; `carried` takes it back). Robust profile: while the copies are apart (see
 * copiesApart), every copy shifted by whole ticks must meet the same surface at its own tick and
 * place, or none, as this run does (sliding along the surface touched last tick, or walking off
 * it, needs no check: every copy that has landed is this run).
 */
function findContactMoving(
  world: World,
  s: PlayerState,
  halfW: number,
  profile: Readonly<CollisionProfile>,
  lead: number,
  prevLead: number,
  carried: number,
  dir: number,
  wasTouching: boolean,
): number {
  const before = prevLead - carried;
  const exact = contactOf(world, s.x, halfW, lead, before, dir, s.tick, false);
  const shifts = copiesApart(profile, s);
  if (shifts === 0 || wasTouching) return exact;
  const step = SPEEDS[s.speed] * DT;
  for (let k = -shifts; k <= shifts; k++) {
    if (k === 0) continue;
    const copy = contactOf(world, s.x + k * step, halfW, lead, before, dir, s.tick + k, true);
    if (Number.isNaN(copy) !== Number.isNaN(exact) || Math.abs(copy - exact) >= SAME_HEIGHT) {
      return UNSAFE_CONTACT;
    }
  }
  return exact;
}

/**
 * The nearest surface (NaN: none) that a box of half-width `halfW` centred at x crossed with its
 * leading edge (from `before` to `lead`) at `tick`: still blocks, and moving blocks relative to
 * where they were a tick before. A timing-shifted `copy` meets nothing whose surface it had
 * passed already (as findContact: it walked off that edge with this run).
 */
function contactOf(
  world: World,
  x: number,
  halfW: number,
  lead: number,
  before: number,
  dir: number,
  tick: number,
  copy: boolean,
): number {
  let best = Number.NaN;
  const solids = world.solids;
  const n = solids.index.query(x - halfW, x + halfW);
  for (let i = 0; i < n; i++) {
    const id = solids.index.found[i]!;
    if (!(solids.x1[id]! < x + halfW - EPS && solids.x2[id]! > x - halfW + EPS)) continue;
    const surface = facingSurface(solids, id, dir);
    if (!crossedSurface(surface, lead, before, dir)) continue;
    if (copy && dir * (before - surface) > 0) continue;
    if (Number.isNaN(best) || dir * surface < dir * best) best = surface;
  }
  const index = world.moverIndex;
  const m = index.query(x - halfW, x + halfW);
  for (let i = 0; i < m; i++) {
    const id = index.found[i]!;
    const now = world.moverBox(id, tick, BOX);
    if (!(now.x1 < x + halfW - EPS && now.x2 > x - halfW + EPS)) continue;
    const surface = dir < 0 ? now.y2 : now.y1;
    const then = world.moverBox(id, tick - 1, PREV_BOX);
    const surfaceBefore = dir < 0 ? then.y2 : then.y1;
    if (!(dir * (lead - surface) > 0 && dir * (before - surfaceBefore) <= LAND_TOLERANCE)) continue;
    if (copy && dir * (before - surfaceBefore) > 0) continue;
    if (Number.isNaN(best) || dir * surface < dir * best) best = surface;
  }
  return best;
}

/** Whole ticks a timing-shifted copy may be off by: margin / speed (see checkActionTiming). */
function shiftTicks(margin: number, speed: SpeedKey): number {
  return Math.floor(margin / (SPEEDS[speed] * DT) + 1e-9);
}

/**
 * Robust profile: how many whole ticks the timing-shifted copies of this run may be apart (0 when
 * they are all this run: no timed action since the last one settled, see settleDown).
 */
function copiesApart(profile: Readonly<CollisionProfile>, s: PlayerState): number {
  return profile.margin > 0 && s.settle > 0 ? shiftTicks(profile.margin, s.speed) : 0;
}

/**
 * True if, for every shift x' in [x - margin, x + margin], the shifted box (half-width
 * halfW) overlaps some solid for which `counts` holds. Reuses the candidate list of the
 * last query on `solids.index` (`n` entries).
 */
function coversAllShifts(
  solids: BoxSet,
  n: number,
  x: number,
  halfW: number,
  margin: number,
  counts: (id: number) => boolean,
): boolean {
  // Sweep the shift interval from the left; each solid covers the open interval of
  // shifts (x1 - halfW, x2 + halfW). Candidate lists are tiny, so repeated passes are cheap.
  let covered = x - margin;
  const target = x + margin;
  for (let pass = 0; pass < n && covered <= target; pass++) {
    let advanced = false;
    for (let i = 0; i < n; i++) {
      const id = solids.index.found[i]!;
      const from = solids.x1[id]! - halfW;
      const to = solids.x2[id]! + halfW;
      if (from + EPS < covered && to > covered && counts(id)) {
        covered = to;
        advanced = true;
      }
    }
    if (!advanced) break;
  }
  return covered > target + EPS;
}

/** The face of a solid that a player moving in world direction `dir` (±1) meets: the top when moving down. */
function facingSurface(solids: BoxSet, id: number, dir: number): number {
  return dir < 0 ? solids.y2[id]! : solids.y1[id]!;
}

/** The face of a solid the player can stand on with the given gravity. */
function floorFace(solids: BoxSet, id: number, gravity: number): number {
  return facingSurface(solids, id, -gravity);
}

/** Height of the player's feet (the bottom of the box, or its top under reversed gravity). */
function feetY(s: PlayerState): number {
  return s.y - (s.gravity * MODE_BOXES[s.mode].outer.h) / 2;
}

// ---------------------------------------------------------------------------
// 5. Overlap tests
// ---------------------------------------------------------------------------

/** Does the player's box of the given size (widened by the profile margins) overlap any box of the set? */
function overlapsBoxes(
  set: BoxSet,
  s: PlayerState,
  size: { readonly w: number; readonly h: number },
  profile: Readonly<CollisionProfile>,
): boolean {
  const hx = size.w / 2 + profile.margin;
  const hy = size.h / 2 + Math.min(profile.marginY, MARGIN_Y_CAP[s.mode]);
  const x1 = s.x - hx;
  const x2 = s.x + hx;
  const y1 = s.y - hy;
  const y2 = s.y + hy;
  const n = set.index.query(x1, x2);
  for (let i = 0; i < n; i++) {
    const id = set.index.found[i]!;
    if (
      set.x1[id]! < x2 - EPS &&
      set.x2[id]! > x1 + EPS &&
      set.y1[id]! < y2 - EPS &&
      set.y2[id]! > y1 + EPS
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Does the player's box of the given size overlap a moving block where it is at the step's tick?
 * Robust profile: every copy shifted by whole ticks while the copies are apart, at its own tick
 * and place (see copiesApart); the vertical margin as for still blocks.
 */
function hitsMovers(
  world: World,
  s: PlayerState,
  size: { readonly w: number; readonly h: number },
  profile: Readonly<CollisionProfile>,
): boolean {
  const hx = size.w / 2;
  const hy = size.h / 2 + Math.min(profile.marginY, MARGIN_Y_CAP[s.mode]);
  const shifts = copiesApart(profile, s);
  const step = SPEEDS[s.speed] * DT;
  const index = world.moverIndex;
  const left = s.x - hx - shifts * step;
  const right = s.x + hx + shifts * step;
  const n = index.query(left, right);
  for (let i = 0; i < n; i++) {
    const id = index.found[i]!;
    // Nowhere near at any tick: nothing to place.
    if (world.moverFrom[id]! >= right || world.moverTo[id]! <= left) continue;
    if (world.moverLow[id]! >= s.y + hy || world.moverHigh[id]! <= s.y - hy) continue;
    for (let k = -shifts; k <= shifts; k++) {
      const b = world.moverBox(id, s.tick + k, BOX);
      const x = s.x + k * step;
      if (
        b.x1 < x + hx - EPS &&
        b.x2 > x - hx + EPS &&
        b.y1 < s.y + hy - EPS &&
        b.y2 > s.y - hy + EPS
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Does the player's box of the given size touch a saw's or a rotor's deadly circles? A still saw
 * is like a spike (the box widened by the margins covers every copy); what moves is met as
 * hitsMovers meets moving blocks: every copy shifted by whole ticks at its own tick and place.
 */
function hitsRounds(
  world: World,
  s: PlayerState,
  size: { readonly w: number; readonly h: number },
  profile: Readonly<CollisionProfile>,
): boolean {
  const hx = size.w / 2;
  const hy = size.h / 2 + Math.min(profile.marginY, MARGIN_Y_CAP[s.mode]);
  const m = profile.margin;
  const shifts = copiesApart(profile, s);
  const step = SPEEDS[s.speed] * DT;
  const reach = hx + Math.max(m, shifts * step);
  const index = world.roundIndex;
  const n = index.query(s.x - reach, s.x + reach);
  for (let i = 0; i < n; i++) {
    const id = index.found[i]!;
    // Nowhere near at any tick: nothing to place.
    if (world.roundFrom[id]! >= s.x + reach || world.roundTo[id]! <= s.x - reach) continue;
    if (world.roundLow[id]! >= s.y + hy || world.roundHigh[id]! <= s.y - hy) continue;
    if (!world.rounds[id]!.moving) {
      const count = world.roundCircles(id, s.tick, CIRCLES);
      if (boxHitsCircles(s.x - hx - m, s.y - hy, s.x + hx + m, s.y + hy, count)) return true;
      continue;
    }
    for (let k = -shifts; k <= shifts; k++) {
      const count = world.roundCircles(id, s.tick + k, CIRCLES);
      const x = s.x + k * step;
      if (boxHitsCircles(x - hx, s.y - hy, x + hx, s.y + hy, count)) return true;
    }
  }
  return false;
}

/**
 * Does the player's box of the given size overlap a boss's attack where it is at the step's tick
 * (while it is there)? Robust profile: every copy shifted by whole ticks while the copies are
 * apart, at its own tick and place, as hitsMovers meets moving blocks.
 */
function hitsAttacks(
  world: World,
  s: PlayerState,
  size: { readonly w: number; readonly h: number },
  profile: Readonly<CollisionProfile>,
): boolean {
  const hx = size.w / 2;
  const hy = size.h / 2 + Math.min(profile.marginY, MARGIN_Y_CAP[s.mode]);
  const shifts = copiesApart(profile, s);
  const step = SPEEDS[s.speed] * DT;
  const index = world.attackIndex;
  const left = s.x - hx - shifts * step;
  const right = s.x + hx + shifts * step;
  const n = index.query(left, right);
  for (let i = 0; i < n; i++) {
    const id = index.found[i]!;
    // Nowhere near at any tick, or not there at any of the copies' ticks: nothing to place.
    if (world.attackFrom[id]! >= right || world.attackTo[id]! <= left) continue;
    if (world.attackLow[id]! >= s.y + hy || world.attackHigh[id]! <= s.y - hy) continue;
    const a = world.attacks[id]!;
    if (a.from > s.tick + shifts || a.to <= s.tick - shifts) continue;
    for (let k = -shifts; k <= shifts; k++) {
      if (!world.attackBox(id, s.tick + k, BOX)) continue;
      const x = s.x + k * step;
      if (
        BOX.x1 < x + hx - EPS &&
        BOX.x2 > x - hx + EPS &&
        BOX.y1 < s.y + hy - EPS &&
        BOX.y2 > s.y - hy + EPS
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Does the box overlap any of the first `count` circles in CIRCLES (touching is not overlapping)? */
function boxHitsCircles(x1: number, y1: number, x2: number, y2: number, count: number): boolean {
  for (let i = 0; i < count; i++) {
    const cx = CIRCLES[3 * i]!;
    const cy = CIRCLES[3 * i + 1]!;
    const r = CIRCLES[3 * i + 2]!;
    const dx = cx < x1 ? x1 - cx : cx > x2 ? cx - x2 : 0;
    const dy = cy < y1 ? y1 - cy : cy > y2 ? cy - y2 : 0;
    if (dx * dx + dy * dy < r * r - EPS) return true;
  }
  return false;
}

/**
 * Does the box of the given size, centred at (x, s.y) and widened horizontally by `margin`, touch
 * a saw that stands still? (A spider's copies running on before a late teleport; anything that
 * moves near one makes the teleport unsafe anyway.)
 */
function stillRoundsAt(
  world: World,
  s: Readonly<PlayerState>,
  x: number,
  size: { readonly w: number; readonly h: number },
  margin: number,
): boolean {
  const hx = size.w / 2 + margin;
  const hy = size.h / 2;
  const index = world.roundIndex;
  const n = index.query(x - hx, x + hx);
  for (let i = 0; i < n; i++) {
    const id = index.found[i]!;
    if (world.rounds[id]!.moving) continue;
    const count = world.roundCircles(id, s.tick, CIRCLES);
    if (boxHitsCircles(x - hx, s.y - hy, x + hx, s.y + hy, count)) return true;
  }
  return false;
}

/**
 * Is anything moving or turning (a moving block, a moving saw, a rotor) or an attack within
 * `reach` of x?
 */
function movesNear(world: World, x: number, reach: number): boolean {
  return (
    world.moverWithin(x - reach, x + reach) ||
    world.roundWithin(x - reach, x + reach, 'moving') ||
    world.attackWithin(x - reach, x + reach)
  );
}

/**
 * Does the box of the given size, centred at (x, s.y) and widened horizontally by `margin`,
 * overlap any box of the set? (A spider's copies running on before a late teleport.)
 */
function overlapsAt(
  set: BoxSet,
  s: Readonly<PlayerState>,
  x: number,
  size: { readonly w: number; readonly h: number },
  margin: number,
): boolean {
  const hx = size.w / 2 + margin;
  const hy = size.h / 2;
  const n = set.index.query(x - hx, x + hx);
  for (let i = 0; i < n; i++) {
    const id = set.index.found[i]!;
    if (
      set.x1[id]! < x + hx - EPS &&
      set.x2[id]! > x - hx + EPS &&
      set.y1[id]! < s.y + hy - EPS &&
      set.y2[id]! > s.y - hy + EPS
    ) {
      return true;
    }
  }
  return false;
}

function kill(s: PlayerState, cause: DeathCause, events: PhysicsEvent[] | undefined): void {
  s.alive = false;
  s.deathCause = cause;
  events?.push({ type: 'death', cause, x: s.x, y: s.y });
}
